import { z, type ZodType } from "zod";

const SESSION_KEY = "synapse.admin.session";
let memoryKey = "";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function restoreSessionKey(): string {
  memoryKey = sessionStorage.getItem(SESSION_KEY) ?? "";
  return memoryKey;
}

export function setMasterKey(key: string, rememberForSession: boolean): void {
  memoryKey = key;
  if (rememberForSession) {
    sessionStorage.setItem(SESSION_KEY, key);
  } else {
    sessionStorage.removeItem(SESSION_KEY);
  }
}

export function clearMasterKey(): void {
  memoryKey = "";
  sessionStorage.removeItem(SESSION_KEY);
}

export async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  init?: RequestInit
): Promise<T> {
  const headers = authorizedHeaders(init?.headers);
  headers.set("accept", "application/json");
  if (init?.body && !headers.has("content-type")) headers.set("content-type", "application/json");

  const response = await fetch(path, { ...init, headers });
  if (!response.ok) await throwApiError(response);
  if (response.status === 204) return schema.parse(null);
  return schema.parse(await response.json());
}

export async function apiDownload(path: string): Promise<Blob> {
  const response = await fetch(path, { headers: authorizedHeaders() });
  if (!response.ok) await throwApiError(response);
  return response.blob();
}

function authorizedHeaders(initial?: HeadersInit): Headers {
  const headers = new Headers(initial);
  if (memoryKey) headers.set("authorization", `Bearer ${memoryKey}`);
  return headers;
}

async function throwApiError(response: Response): Promise<never> {
  const payload: unknown = await response.json().catch(() => null);
  const parsed = errorEnvelope.safeParse(payload);
  throw new ApiError(
    response.status,
    parsed.success ? parsed.data.error.code : "request_failed",
    parsed.success
      ? parsed.data.error.message
      : `Request failed with status ${String(response.status)}`
  );
}

const errorEnvelope = z.object({
  error: z.object({
    code: z.string(),
    message: z.string()
  })
});
